import { Routes } from '@angular/router';
import { AccountEnabledGuard, AuthFormEmailGuard, SignedInGuard } from '@platform-frontend/account';
import { LicenseScopeGuard, StashRequiresConfirmationGuard, SubscriptionBookingStatusGuard, UserTypeGuard } from '@platform-frontend/checkout';
import { AcademicRegistrationStartupGuard } from './academic-registration/academic-registration-startup.guard';
import { AcademicRegistrationComponent } from './academic-registration/academic-registration/academic-registration.component';
import { ConfirmAcademicEmailStartupGuard } from './academic-registration/confirmation/confirm-academic-email-startup.guard';
import { ConfirmationComponent as AcademicConfirmationComponent } from './academic-registration/confirmation/confirmation.component';
import { IpRangeComponent } from './academic-registration/ip-range/ip-range.component';
import { StudentDetailsComponent } from './academic-registration/student-details/student-details.component';
import { TeacherToggleComponent } from './academic-registration/teacher-toggle/teacher-toggle.component';
import { VerifyAcademicEmailComponent } from './academic-registration/verify-academic-email/verify-academic-email.component';
import { VerifyEmployeeComponent } from './academic-registration/verify-employee/verify-employee.component';
import { BillingAddressSelectionComponent } from './commercial-checkout/billing-address-selection/billing-address-selection.component';
import { BillingAddressComponent } from './commercial-checkout/billing-address/billing-address.component';
import { CommercialCheckoutStartupGuard } from './commercial-checkout/commercial-checkout-startup.guard';
import { ConfirmationComponent } from './commercial-checkout/confirmation/confirmation.component';
import { OverviewComponent } from './commercial-checkout/overview/overview.component';
import { PaymentMethodSelectionComponent } from './commercial-checkout/payment-method-selection/payment-method-selection.component';
import { CreatePersonComponent } from './commercial-checkout/personalization/create-person/create-person.component';
import { MultiplePersonComponent } from './commercial-checkout/personalization/multiple-person/multiple-person.component';
import { AddPaymentMethodCommonComponent } from './commercial-common/add-payment-method-common/add-payment-method-common.component';
import { PaymentMethodWithDataComponent } from './commercial-common/payment-method-with-data/payment-method-with-data.component';
import { PaymentMethodWithPollingComponent } from './commercial-common/payment-method-with-polling/payment-method-with-polling.component';
import { ScaAuthenticationComponent } from './commercial-common/sca-authentication/sca-authentication.component';
import { ScaSpinnerComponent } from './commercial-common/sca-spinner/sca-spinner.component';
import { ScaGuard } from './commercial-common/sca.guard';
import { EmptyComponent } from './integration/empty/empty.component';
import { EmptyGuard } from './integration/empty/empty.guard';
import { ErrorComponent } from './layout/error/error.component';
import { OverviewComponent as PurchaseInformationOverviewComponent } from './purchase-information/overview/overview.component';
import { PurchaseInformationStartupGuard } from './purchase-information/purchase-information-startup.guard';
import { AddPaymentMethodComponent as SubscriptionManagementAddPaymentMethodComponent } from './subscription-management/add-payment-method/add-payment-method.component';
import { BillingAddressSelectionComponent as SubscriptionManagementBillingAddressSelectionComponent } from './subscription-management/billing-address-selection/billing-address-selection.component';
import { BillingAddressComponent as SubscriptionManagementBillingAddressComponent } from './subscription-management/billing-address/billing-address.component';
import { CancelConfirmationComponent } from './subscription-management/cancel-confirmation/cancel-confirmation.component';
import { CancelSubscriptionComponent } from './subscription-management/cancel-subscription/cancel-subscription.component';
import { OverviewComponent as SubscriptionManagementOverviewComponent } from './subscription-management/overview/overview.component';
import { PaymentMethodSelectionComponent as SubscriptionManagementPaymentMethodSelectionComponent } from './subscription-management/payment-method-selection/payment-method-selection.component';
import { SubscriptionManagementStartupGuard } from './subscription-management/subscription-management-startup.guard';
import { UpdateConfirmationComponent } from './subscription-management/update-confirmation/update-confirmation.component';

export const APP_ROUTES: Routes = [
  {
    path: 'academic-registration/:startParams',
    canActivate: [AcademicRegistrationStartupGuard],
    canActivateChild: [SignedInGuard],
    children: [
      {
        path: 'academic-registration',
        component: AcademicRegistrationComponent
      },
      {
        path: 'confirmation',
        component: AcademicConfirmationComponent,
        data: { isAutoClosePage: true }
      },
      {
        path: 'ip-range',
        canActivate: [UserTypeGuard, LicenseScopeGuard],
        component: IpRangeComponent,
        data: { allowedUserTypes: ['TEACHER', 'ORGANIZATION'], allowedLicenseScopes: ['CONCURRENT_USE'] }
      },
      {
        path: 'student-details',
        canActivate: [UserTypeGuard, LicenseScopeGuard],
        component: StudentDetailsComponent,
        data: { allowedUserTypes: ['STUDENT'], allowedLicenseScopes: ['PERSONAL_USE'] }
      },
      {
        path: 'teacher-toggle',
        canActivate: [UserTypeGuard],
        component: TeacherToggleComponent,
        data: { allowedUserTypes: ['TEACHER'] }
      },
      {
        path: 'verify-academic-email',
        component: VerifyAcademicEmailComponent,
        data: { replaceUrl: true }
      },
      {
        path: 'verify-employee',
        canActivate: [UserTypeGuard],
        component: VerifyEmployeeComponent,
        data: { allowedUserTypes: ['TEACHER', 'ORGANIZATION'] }
      },
      {
        path: '**',
        pathMatch: 'full',
        redirectTo: 'academic-registration'
      }
    ],
    data: { header: 'academic', headerExpanded: true }
  },
  {
    path: 'academic-confirmation',
    canActivate: [AuthFormEmailGuard, ConfirmAcademicEmailStartupGuard],
    component: AcademicConfirmationComponent,
    data: { header: 'academic', headerExpanded: true }
  },
  {
    path: 'account-disabled',
    loadChildren: () => import('@platform-frontend/account-disabled').then(m => m.AccountDisabledModule)
  },
  {
    path: 'authentication',
    loadChildren: () => import('@platform-frontend/authentication').then(m => m.AuthenticationModule)
  },
  {
    path: 'back-to-empty',
    component: EmptyComponent,
    canActivate: [EmptyGuard],
    data: { goBack: true, hidden: true }
  },
  {
    path: 'checkout/:startParams',
    canActivate: [CommercialCheckoutStartupGuard],
    canActivateChild: [SignedInGuard],
    children: [
      {
        path: 'add-payment-method',
        component: AddPaymentMethodCommonComponent
      },
      {
        path: 'billing-address',
        component: BillingAddressComponent
      },
      {
        path: 'billing-address-selection',
        component: BillingAddressSelectionComponent
      },
      {
        path: 'confirmation',
        component: ConfirmationComponent,
        data: { isAutoClosePage: true, isSuccessPage: true, matomoTitle: 'Web Checkout Success Page' }
      },
      { path: 'create-person', component: CreatePersonComponent },
      { path: 'multiple-person', component: MultiplePersonComponent },
      {
        path: 'overview',
        component: OverviewComponent,
        data: { container: false, headerExpanded: true, matomoTitle: 'Web Checkout Overview Page' }
      },
      {
        path: 'payment-method-selection',
        component: PaymentMethodSelectionComponent
      },
      {
        path: 'payment-method-with-data',
        component: PaymentMethodWithDataComponent
      },
      {
        path: 'payment-method-with-polling',
        component: PaymentMethodWithPollingComponent,
        data: { replaceUrl: true }
      },
      {
        path: 'sca-authentication',
        canActivate: [ScaGuard],
        component: ScaAuthenticationComponent,
        data: { replaceUrl: true }
      },
      {
        path: 'sca-spinner',
        canActivate: [ScaGuard],
        component: ScaSpinnerComponent,
        data: { replaceUrl: true }
      },
      {
        path: '**',
        pathMatch: 'full',
        redirectTo: 'overview'
      }
    ],
    data: { header: 'commercial' }
  },
  {
    path: 'empty',
    component: EmptyComponent,
    canActivate: [EmptyGuard],
    data: { hidden: true }
  },
  {
    path: 'error',
    component: ErrorComponent,
    data: { header: 'none', notifications: false, isErrorPage: true }
  },
  {
    path: 'purchase-information/:startParams',
    canActivate: [PurchaseInformationStartupGuard],
    canActivateChild: [SignedInGuard],
    children: [
      {
        path: 'overview',
        component: PurchaseInformationOverviewComponent
      },
      {
        path: '**',
        pathMatch: 'full',
        redirectTo: 'overview'
      }
    ],
    data: { header: 'commercial' }
  },
  {
    path: 'subscription-management/:startParams',
    canActivate: [SubscriptionManagementStartupGuard],
    canActivateChild: [SignedInGuard],
    children: [
      {
        path: 'add-payment-method',
        component: SubscriptionManagementAddPaymentMethodComponent
      },
      {
        path: 'billing-address',
        component: SubscriptionManagementBillingAddressComponent
      },
      {
        path: 'billing-address-selection',
        component: SubscriptionManagementBillingAddressSelectionComponent
      },
      {
        path: 'cancel-confirmation',
        canActivate: [AccountEnabledGuard],
        component: CancelConfirmationComponent,
        data: { isAutoClosePage: true }
      },
      {
        path: 'cancel-subscription',
        canActivate: [AccountEnabledGuard, SubscriptionBookingStatusGuard],
        component: CancelSubscriptionComponent,
        data: { allowedBookingStatus: ['BOOKED', 'SUBSCRIBED'] }
      },
      {
        path: 'overview',
        component: SubscriptionManagementOverviewComponent,
        data: { headerExpanded: true }
      },
      {
        path: 'payment-method-selection',
        component: SubscriptionManagementPaymentMethodSelectionComponent
      },
      {
        path: 'payment-method-with-data',
        component: PaymentMethodWithDataComponent
      },
      {
        path: 'payment-method-with-polling',
        component: PaymentMethodWithPollingComponent,
        data: { replaceUrl: true }
      },
      {
        path: 'sca-authentication',
        canActivate: [ScaGuard],
        component: ScaAuthenticationComponent,
        data: { replaceUrl: true }
      },
      {
        path: 'sca-spinner',
        canActivate: [ScaGuard],
        component: ScaSpinnerComponent,
        data: { replaceUrl: true }
      },
      {
        path: 'update-confirmation',
        canActivate: [StashRequiresConfirmationGuard],
        component: UpdateConfirmationComponent,
        data: { container: false, headerExpanded: true }
      },
      {
        path: '**',
        pathMatch: 'full',
        redirectTo: 'overview'
      }
    ],
    data: { header: 'commercial' }
  },
  {
    path: '**',
    pathMatch: 'full',
    redirectTo: 'empty'
  }
];
